'use client'

import { setCookie } from 'cookies-next'
import { ArrowLeftIcon, CheckCircle2, Loader2, RefreshCw } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { QRCodeCanvas } from 'qrcode.react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  useIsAndroidClient,
  useIsIOSClient,
  useIsMobileClient,
} from '@/hooks/common/use-device-type'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent } from '@/lib/gtm/type'
import { cn } from '@/lib/utils'
import { ImageCM } from '../extension/image-cm'
import { Button } from '../ui/button'

export default function QRLogin({
  appId = '2973872234761103313',
  redirectUrl = '/',
  onBack,
}: {
  appId?: string
  redirectUrl?: string
  onBack?: () => void
}) {
  const [sid, setSid] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('qr_login_sid')
      if (stored) return stored
      const newSid = uuidv4()
      sessionStorage.setItem('qr_login_sid', newSid)
      return newSid
    }
    return uuidv4()
  })
  const [status, setStatus] = useState<'waiting' | 'success' | 'expired'>(
    'waiting'
  )

  // Lưu mốc thời gian hết hạn (Timestamp)
  const [expiryTime, setExpiryTime] = useState<number>(
    () => Date.now() + 300000
  ) // Now + 5 mins
  const [timeLeft, setTimeLeft] = useState(300)
  const t = useTranslations('qr_login')
  const trackEvent = useTrackEvent()
  const isMobile = useIsMobileClient()
  const isAndroid = useIsAndroidClient()
  const isIOS = useIsIOSClient()

  // useTrackEvent trả về function mới mỗi render -> giữ qua ref để không làm effect polling chạy lại
  const trackEventRef = useRef(trackEvent)
  trackEventRef.current = trackEvent
  // Cờ đã đăng nhập phải sống qua các lần render (biến cục bộ trong effect sẽ bị reset khi effect chạy lại)
  const authenticatedRef = useRef(false)

  // Dùng handleRefresh để reset lại mốc thời gian tuyệt đối
  const handleRefresh = useCallback(() => {
    const newExpiry = Date.now() + 300000
    const newSid = uuidv4()
    sessionStorage.setItem('qr_login_sid', newSid)
    setSid(newSid)
    setExpiryTime(newExpiry)
    setTimeLeft(300)
    authenticatedRef.current = false
    setStatus('waiting')
  }, [])

  useEffect(() => {
    let inFlight = false

    const checkStatus = async () => {
      // Không gọi chồng request khi request trước chưa trả về
      if (authenticatedRef.current || inFlight) return
      inFlight = true
      try {
        const response = await fetch(`/api/auth/qr-confirm/poll?sid=${sid}`)
        const data = await response.json()

        if (data.status === 'authenticated' && !authenticatedRef.current) {
          authenticatedRef.current = true
          setStatus('success')
          trackEventRef.current({
            event: ETrackingEvent.ClickTracking,
            event_category: 'click_tracking',
            event_label: 'qr_login_success',
            link_class: 'qr_login_success',
            event_value: 1,
          })
          setCookie(
            process.env.NEXT_PUBLIC_TOKEN_KEY || 'auth_token',
            data.token,
            {
              maxAge: 30 * 24 * 60 * 60,
              path: '/',
              // Thêm 2 dòng này để Android Chrome không chặn
              sameSite: 'none',
              secure: true,
            }
          )
          setTimeout(() => {
            window.location.href = redirectUrl
          }, 1500)
        }
      } catch (err) {
        console.error('Polling error, retrying...', err)
      } finally {
        inFlight = false
      }
    }

    // 1. Polling định kỳ mỗi 1 giây
    const pollInterval = setInterval(checkStatus, 1000)

    // 2. ĐẶC BIỆT CHO ANDROID: Tự động kiểm tra ngay khi người dùng quay lại trình duyệt
    const handleFocus = () => {
      checkStatus()
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkStatus()
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibility)

    // LOGIC ĐẾM NGƯỢC DỰA TRÊN THỜI GIAN THỰC
    const timer = setInterval(() => {
      if (authenticatedRef.current) {
        clearInterval(timer)
        return
      }
      const now = Date.now()
      const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000))

      setTimeLeft(remaining)

      if (remaining <= 0) {
        clearInterval(timer)
        setStatus('expired')
      }
    }, 1000) // Vẫn chạy mỗi 1s, nhưng nếu tab bị ẩn, lần chạy tới nó sẽ nhảy vọt số liệu cho đúng

    return () => {
      clearInterval(pollInterval)
      clearInterval(timer)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [sid, expiryTime, redirectUrl])

  // Track QR View when SID changes (new QR generated)
  useEffect(() => {
    trackEventRef.current({
      event: ETrackingEvent.ClickTracking,
      event_category: 'click_tracking',
      event_label: 'qr_login_view',
      link_class: 'qr_login_view',
      event_value: 1,
    })
  }, [sid])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // 1. Lấy origin (ví dụ: https://v2.coolmate.io)
  const currentOrigin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://www.coolmate.me'

  // 2. Mã hóa an toàn
  const encodedDomain = encodeURIComponent(currentOrigin)
  const encodedSid = encodeURIComponent(sid)

  // 3. Ghép vào URL QR
  const qrValue =
    process.env.NEXT_PUBLIC_ENV === 'local'
      ? `https://zalo.me/app/link/zapps/${appId}/qrlogin-callback/?env=TESTING_LOCAL&sid=${encodedSid}&domain=${encodedDomain}`
      : `https://zalo.me/s/${appId}/qrlogin-callback/?sid=${encodedSid}&domain=${encodedDomain}`

  // --- Giữ nguyên phần Render UI của bạn ở đây ---
  return (
    <div className='flex flex-col items-center'>
      <div className='relative mx-auto w-fit'>
        <div className='mt-4 flex items-center justify-center gap-4 text-neutral-800'>
          {onBack && (
            <Button
              onClick={onBack}
              aria-label={t('back')}
              className='absolute left-4 top-2 h-10 w-10 rounded-full p-1'
              variant='outline-gray'
            >
              <ArrowLeftIcon className='size-6' />
            </Button>
          )}
          <div className='flex flex-col items-center'>
            <ImageCM
              src={'/March2024/mceclip4_81.jpg'}
              width={132}
              height={22}
              alt={t('logo_alt')}
            />
            <h3 className='mx-4 mt-4 text-center font-criteria text-xl font-medium leading-8 tracking-normal text-neutral-900'>
              {t('title')}
            </h3>
          </div>
        </div>
        <div className='flex flex-col items-center p-4 text-center'>
          {(isMobile || isAndroid || isIOS) && (
            <a
              href={qrValue}
              className='mb-4 flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 active:scale-95'
            >
              <ImageCM
                src={'/2026/01/22/mceclip0_53.png'}
                width={24}
                height={24}
                alt='Zalo'
                className='rounded-full'
              />
              {t('open_zalo')}
            </a>
          )}
          <div className='relative mb-2 rounded-2xl bg-white p-2 shadow-[0_0_20px_rgba(0,0,0,0.05)] ring-1 ring-slate-100'>
            <div
              className={cn(
                status !== 'waiting' ? 'blur-[2px] grayscale' : '',
                'w-fit max-w-[180px] md:max-w-[240px]'
              )}
            >
              <QRCodeCanvas
                value={qrValue}
                size={220}
                level='H'
                style={{ width: '100%', height: 'auto' }}
                imageSettings={{
                  src: 'https://mcdn.coolmate.me/uploads/January2024/Coolmate_Logo_New_Black.png',
                  height: 40,
                  width: 40,
                  excavate: true,
                }}
              />
            </div>

            {status === 'success' && (
              <div className='absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-white/90 backdrop-blur-[2px]'>
                <CheckCircle2 className='h-16 w-16 text-green-500 duration-300 animate-in zoom-in' />
                <span className='mt-2 font-semibold text-green-600'>
                  {t('success')}
                </span>
              </div>
            )}

            {status === 'expired' && (
              <div className='absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-white/95 backdrop-blur-[4px]'>
                <button
                  onClick={handleRefresh}
                  className='group flex flex-col items-center justify-center space-y-2'
                >
                  <div className='rounded-full bg-slate-900 p-4 text-white shadow-lg group-hover:bg-slate-800'>
                    <RefreshCw className='h-8 w-8' />
                  </div>
                  <span className='text-sm font-bold text-slate-900'>
                    {t('refresh')}
                  </span>
                </button>
              </div>
            )}
          </div>

          <div className='flex w-full items-center justify-center gap-3 rounded-full bg-slate-50 py-2'>
            {status === 'waiting' ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin text-primary-500' />
                <div className='flex items-center gap-2 text-xs font-medium text-slate-400'>
                  <span>{t('expired_after')}:</span>
                  <span className='font-mono text-slate-900'>
                    {formatTime(timeLeft)}
                  </span>
                </div>
              </>
            ) : status === 'expired' ? (
              <span className='text-sm font-medium text-red-500'>
                {t('expired')}
              </span>
            ) : (
              <span className='text-sm font-medium text-green-600'>
                {t('redirecting')}
              </span>
            )}
          </div>

          <p className='mt-2 text-sm italic text-neutral-600'>
            {t('qr_instruction')}
          </p>
        </div>
      </div>

      <Button className='mt-4 w-fit' variant='ghost' onClick={onBack}>
        {t('back')}
      </Button>
    </div>
  )
}
